import React from 'react';
import { CARD_STYLES, BUTTON_STYLES, selectChevron } from 'constants/styles';
import { MEAL_PRICES } from 'constants/meals';
import { ValidationWarning } from 'components/mealplan/StatusMessages';
import { allergenConflictLabel } from 'utils/allergenLabels';

const CompactMealCard = ({
    meal,
    actualIndex,
    onOpenModal,
    onQuantityChange,
    isLocked,
    saving,
    // MealPlanViewer passes false: Lunch/Dinner are covered by the subscription there.
    showPrice = true,
    // false for the subscription builder's add-on cards: with AddonInfoModal now
    // available (click the card to see ingredients/details), the inline listing
    // is redundant clutter there. Every other caller keeps it — defaulting to
    // true preserves today's behavior everywhere this isn't explicitly passed.
    showIngredients = true,
    // Clamp the ingredient listing to N lines with a CSS ellipsis instead of
    // hiding it outright — customers care about seeing ingredients up front,
    // per the boss, so MealPlanViewer uses this (3) rather than
    // showIngredients={false}. null/undefined means no clamp (today's
    // behavior). Has no effect when showIngredients is false.
    ingredientLineClamp = null,
    // Storefront-only below — its add button opens the modal instead of adding straight away.
    addLabel = 'Add',
    // [{ mealType, price }] — the portions this dish can be ordered as, empty
    // when it only comes one way.
    mealTypeChoices = [],
    selectedMealType,
    onMealTypeChange,
}) => {
    const warningProteinCount = () => {
        if (!meal.proteinOptions || !Array.isArray(meal.proteinOptions)) return null;
        return meal.proteinOptions.filter(option => option.isActive).length < 1 && meal.proteinOptions.length > 0;
    };

    const warningVeggieCount = () => {
        if (!meal.veggieOptions || !Array.isArray(meal.veggieOptions)) return null;
        return meal.veggieOptions.filter(option => option.isActive).length < 1 && meal.veggieOptions.length > 0;
    };

    const warningStarchCount = () => {
        if (!meal.starchOptions || !Array.isArray(meal.starchOptions)) return null;
        return meal.starchOptions.filter(option => option.isActive).length < 1 && meal.starchOptions.length > 0;
    };

    const warningSauceCount = () => {
        if (!meal.sauceOptions || !Array.isArray(meal.sauceOptions) || meal.meal === 'Add On' || meal.meal === 'Snack' || meal.meal === 'Breakfast') return null;
        return meal.sauceOptions.filter(option => option.isActive).length < 1 && meal.sauceOptions.length > 0;
    };

    const hasValidationWarnings = meal.meal !== 'Snack' && meal.quantity > 0 && (
        warningProteinCount() ||
        warningVeggieCount() ||
        warningStarchCount() ||
        warningSauceCount()
    );

    const allergenNames = allergenConflictLabel(meal.allergenConflicts);

    const getValidationMessages = () => {
        const messages = [];
        if (meal.meal !== 'Snack' && meal.meal !== 'Add On' && meal.quantity > 0) {
            if (warningProteinCount()) messages.push('No Protein Selected');
            if (warningVeggieCount()) messages.push('No Veggie Selected');
            if (warningStarchCount()) messages.push('No Starch Selected');
            if (warningSauceCount()) messages.push('No Sauce Selected');
        }
        return messages;
    };

    return (
        <div
            onClick={() => onOpenModal(meal, actualIndex)}
            data-testid={`dish-card-${actualIndex}`}
            style={{
                ...CARD_STYLES.container(meal),
                border: hasValidationWarnings ? '2px solid #e74c3c' : CARD_STYLES.container(meal).border,
                boxShadow: hasValidationWarnings
                    ? '0 2px 10px rgba(231, 76, 60, 0.2)'
                    : CARD_STYLES.container(meal).boxShadow
            }}
        >
            {meal.imageUrl && (
                <div
                    style={{
                        ...CARD_STYLES.image,
                        backgroundImage: `url(${meal.imageUrl})`,
                        transition: 'transform 0.2s ease',
                        cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                    }}
                />
            )}

            {meal.isTemporary && (
                <div style={{
                    fontSize: '14px',
                    color: '#856404',
                    backgroundColor: 'solid #fff3cd',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    marginBottom: '4px',
                    textAlign: 'center'
                }}>
                    Saving...
                </div>
            )}

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px',
                    flexWrap: 'wrap',
                    gap: '4px'
                }}>
                    <div style={CARD_STYLES.title}>
                        {meal.itemName}
                        {meal.quantity === 0 && (
                            <span style={{
                                marginLeft: '6px',
                                fontSize: '10px',
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontWeight: 'normal',
                                border: '1px solid #1976d2'
                            }}>
                                Available
                            </span>
                        )}
                    </div>

                    {meal.quantity > 0 && (
                        <div style={{
                            fontSize: '13px',
                            color: '#007bff',
                            fontWeight: 'bold',
                            textDecoration: 'underline',
                            pointerEvents: 'none',
                            flexShrink: 0
                        }}>
                            EDIT
                        </div>
                    )}
                </div>

                {showIngredients && <div style={{
                    fontSize: '10px',
                    color: '#666',
                    lineHeight: '1.4',
                    ...(ingredientLineClamp ? {
                        display: '-webkit-box',
                        WebkitLineClamp: ingredientLineClamp,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    } : {}),
                }}>
                    {(() => {
                        const categories = [
                            { label: 'Protein', options: meal.proteinOptions, showSubs: true },
                            { label: 'Starch', options: meal.starchOptions, showSubs: true },
                            { label: 'Vegetables', options: meal.veggieOptions, showSubs: false },
                            { label: 'Sauce', options: meal.sauceOptions, showSubs: true },
                            { label: 'Garnish', options: meal.garnishOptions, showSubs: false },
                        ];
                        // Available dishes (qty 0) have nothing marked active, so the
                        // defaults come from originalIngredients — unless allergy
                        // adjustment already resolved a selection.
                        const originalIds = new Set((meal.originalIngredients || []).map(i => i.id));
                        const useActive = meal.quantity > 0 || meal.selectionResolved;
                        const lines = categories
                            .map(({ label, options, showSubs }) => {
                                if (!options || !Array.isArray(options)) return null;
                                // id null is "No Sauce"/"No Starch" — a selection, not an
                                // ingredient to list (mirrors ingredientSummary).
                                const display = useActive
                                    ? options.filter(o => o.isActive && o.id != null)
                                    : options.filter(o => originalIds.has(o.id));
                                if (display.length === 0) return null;
                                const stripSF = (s) => (s || '').replace(/^SF /, '');
                                const namesList = display.map(o => {
                                    const cleanSubs = (o.subIngredientNames || []).map(stripSF);
                                    const subs = showSubs && cleanSubs.length > 0
                                        ? ` (${cleanSubs.join(', ')})`
                                        : '';
                                    return { name: stripSF(o.name?.trim()), subs, rawSubs: cleanSubs };
                                });
                                return { label, namesList };
                            })
                            .filter(Boolean);

                        if (lines.length === 0) {
                            const ingredients = meal.finalIngredients?.length > 0
                                ? meal.finalIngredients
                                : meal.originalIngredients;
                            return ingredients?.map(i => (i?.name || '').trim()).filter(Boolean).join(', ') || '';
                        }

                        const singleCategory = lines.length === 1;
                        if (singleCategory) {
                            return lines[0].namesList.map((item, i) => {
                                const nameL = item.name?.toLowerCase() || '';
                                const titleL = meal.itemName?.toLowerCase() || '';
                                const sameName = nameL === titleL || titleL.includes(nameL) || nameL.includes(titleL);
                                // Single-component dishes show subs even where the category wouldn't.
                                const subsText = item.subs || (item.rawSubs.length > 0 ? ` (${item.rawSubs.join(', ')})` : '');
                                if (sameName && !subsText) return null;
                                return <div key={i}><span style={{ fontWeight: '600' }}>{item.name}</span>{subsText}</div>;
                            });
                        }
                        return lines.map((line, i) => (
                            <div key={i}>
                                <span style={{ fontWeight: '600' }}>{line.label}:</span> {line.namesList.map(item => item.name + item.subs).join(', ')}
                            </div>
                        ));
                    })()}
                </div>}

                {/* Substitutions run before the customer opens anything, so the card has to
                    say so rather than show a dish the menu describes differently. */}
                {meal.hasAllergenConflict ? (
                    <ValidationWarning
                        message={allergenNames
                            ? `Allergy detected: ${allergenNames}`
                            : 'Allergy detected in this meal'}
                    />
                ) : meal.allergyAdjusted ? (
                    <div style={{ marginTop: '6px' }}>
                        <span style={{
                            display: 'inline-block',
                            fontSize: '11px',
                            fontWeight: '600',
                            backgroundColor: '#e6f4ea',
                            color: '#28371D',
                            padding: '2px 7px',
                            borderRadius: '10px'
                        }}>
                            ↻ Adjusted for your allergies
                        </span>
                    </div>
                ) : null}

                {hasValidationWarnings && !meal.isTemporary && (
                    <ValidationWarning message={getValidationMessages().join(', ')} />
                )}

                <div style={{
                    marginTop: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    paddingTop: '10px'
                }}>
                    {/* One card serves every portion the dish comes in */}
                    {mealTypeChoices.length > 1 && (
                        <select
                            value={selectedMealType || mealTypeChoices[0].mealType}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                                e.stopPropagation();
                                onMealTypeChange?.(e.target.value);
                            }}
                            disabled={isLocked || saving}
                            aria-label="Choose portion"
                            style={{
                                fontSize: '12px',
                                padding: '5px 26px 5px 7px',
                                border: '1px solid #ccc',
                                borderRadius: '5px',
                                backgroundColor: '#fff',
                                color: '#333',
                                width: '100%',
                                marginBottom: '8px',
                                cursor: (isLocked || saving) ? 'not-allowed' : 'pointer',
                                ...selectChevron('8px')
                            }}
                        >
                            {mealTypeChoices.map(({ mealType, price }) => {
                                // The dish's own price for that portion; MEAL_PRICES is
                                // the standard fallback when the menu record has none.
                                const shown = price > 0 ? price : MEAL_PRICES[mealType];
                                return (
                                    <option key={mealType} value={mealType}>
                                        {shown != null ? `${mealType} (from $${Number(shown).toFixed(2)})` : mealType}
                                    </option>
                                );
                            })}
                        </select>
                    )}

                    {/* The portion dropdown already prices each option */}
                    {showPrice && mealTypeChoices.length <= 1 && meal.price > 0 && (
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                            {Array.isArray(meal.proteinOptions) && meal.proteinOptions.length > 1 && meal.proteinOptions.some(p => p.premium)
                                ? `from $${Number(meal.price).toFixed(2)}`
                                : `$${Number(meal.price).toFixed(2)}`}
                        </span>
                    )}

                    {/* Calorie/macro line — the client's goal for this meal type, the
                        not-logged-in default, or a Skip Portioning dish's own fixed
                        nutrition, computed server-side in calorieInfoForDish /
                        calorieInfoForGuestDish, or client-side in calorieInfoForMember
                        for a quiz-taker. Absent entirely when nutrition data is
                        suppressed for this client. Placed after price, not before —
                        see showIngredients for the companion "less clutter" change. */}
                    {meal.calorieInfo?.calories > 0 && (
                        <span style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                            {/* Rounded here too, not just at the source — a decimal shouldn't
                                reach the screen regardless of which path the number came from. */}
                            {Math.round(meal.calorieInfo.calories)} kcals
                            {meal.calorieInfo.carbsG != null && (
                                <> | {Math.round(meal.calorieInfo.carbsG)}g carbs, {Math.round(meal.calorieInfo.proteinG)}g protein, {Math.round(meal.calorieInfo.fatG)}g fat, {Math.round(meal.calorieInfo.fiberG)}g fiber</>
                            )}
                        </span>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '5px' }}>

                        <span style={{ fontSize: '10px', color: '#666' }}>
                            Qty: {meal.quantity}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>

                            {meal.quantity > 0 && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onQuantityChange(actualIndex, Math.max(0, meal.quantity - 1));
                                    }}
                                    disabled={isLocked || saving || meal.quantity <= 0}
                                    style={{
                                        ...BUTTON_STYLES.quantity.remove,
                                        cursor: (isLocked || saving || meal.quantity <= 0) ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    Remove
                                </button>
                            )}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onQuantityChange(actualIndex, meal.quantity + 1);
                                }}
                                disabled={isLocked || saving}
                                data-testid="dish-add-button"
                                style={{
                                    ...BUTTON_STYLES.quantity.add,
                                    cursor: (isLocked || saving) ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {addLabel}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompactMealCard;